import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Copy, Check, Skull, Biohazard } from "lucide-react";
import { CONTRACT_ADDRESS, BUY_URL, X_URL, copyText } from "../../data/constants";
import { getStats } from "../../data/outbreaks";

const INFECT_KEY = "cyclospora_infections";

export const Hero = () => {
  const [copied, setCopied] = useState(false);
  const [infections, setInfections] = useState(0);
  const stats = getStats();

  useEffect(() => {
    let v = localStorage.getItem(INFECT_KEY);
    if (!v) {
      v = String(66666 + Math.floor(Math.random() * 9999));
      localStorage.setItem(INFECT_KEY, v);
    }
    setInfections(Number(v));
  }, []);

  const spread = () => {
    setInfections((prev) => {
      const next = prev + 1;
      localStorage.setItem(INFECT_KEY, String(next));
      return next;
    });
    toast("INFECTION SPREAD!", { description: "One more soul sprinting to the bathroom." });
  };

  const copyCA = async () => {
    const ok = await copyText(CONTRACT_ADDRESS);
    if (ok) {
      setCopied(true);
      toast.success("Contract address copied", { description: CONTRACT_ADDRESS });
      setTimeout(() => setCopied(false), 1600);
    } else {
      toast("Couldn't auto-copy", { description: CONTRACT_ADDRESS });
    }
  };

  return (
    <section id="top" className="relative pt-28 md:pt-36 pb-14 px-4 flex flex-col items-center text-center overflow-hidden" data-testid="hero">
      {/* floating biohazard */}
      <Biohazard
        className="absolute right-4 md:right-10 top-24 w-16 h-16 md:w-28 md:h-28 spin-slow hidden sm:block"
        style={{ color: "#d9b3ff", filter: "drop-shadow(0 0 12px rgba(217,179,255,0.7))" }}
        aria-hidden
      />
      <Biohazard
        className="absolute left-4 md:left-10 top-52 w-12 h-12 md:w-20 md:h-20 spin-slow hidden sm:block"
        style={{ color: "#b026ff", filter: "drop-shadow(0 0 12px rgba(176,38,255,0.7))" }}
        aria-hidden
      />

      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="inline-flex items-center gap-2 bg-[#b026ff] border-2 border-[#0a133f] px-4 py-1 mb-6 blink"
        data-testid="hero-alert-badge"
      >
        <Skull size={16} className="text-[#0a133f]" />
        <span className="font-mono2 font-bold text-xs md:text-sm text-[#0a133f] uppercase tracking-widest">
          Emergency Broadcast · Code Brown
        </span>
      </motion.div>

      <motion.h1
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ type: "spring", stiffness: 120 }}
        className="stencil text-5xl sm:text-7xl lg:text-8xl xl:text-9xl leading-[0.85] max-w-6xl"
        data-testid="hero-title"
      >
        CYCLOSPORIASIS
      </motion.h1>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="mt-5 stencil-pink text-3xl md:text-5xl lg:text-6xl"
        data-testid="hero-tagline"
      >
        the disease that causes explosive diarrhea
      </motion.p>

      {/* CTA row */}
      <div className="mt-8 flex flex-col sm:flex-row items-center gap-4" data-testid="hero-cta-row">
        <a href={BUY_URL} target="_blank" rel="noreferrer" data-testid="hero-buy-btn" className="btn-hazard bg-[#d9b3ff] text-[#0a133f] text-xl md:text-2xl px-8 py-4 pulse-glow">
          BUY THE INFECTION
        </a>
        <a href={X_URL} target="_blank" rel="noreferrer" data-testid="hero-x-btn" className="btn-hazard bg-white text-[#0a133f] text-xl md:text-2xl px-8 py-4">
          X JOIN THE OUTBREAK
        </a>
      </div>

      {/* CA */}
      <button onClick={copyCA} data-testid="hero-copy-ca" className="mt-6 group flex items-center gap-3 petri px-4 py-3 hover-shake max-w-full">
        <span className="font-mono2 text-[10px] md:text-xs text-[#d9b3ff] uppercase">Contract</span>
        <span className="font-mono2 text-[10px] md:text-sm text-white break-all">{CONTRACT_ADDRESS}</span>
        {copied ? <Check size={16} className="text-[#d9b3ff] shrink-0" /> : <Copy size={16} className="text-[#b026ff] shrink-0" />}
      </button>

      {/* live stat cards */}
      <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3 w-full max-w-4xl" data-testid="hero-stats">
        <StatCard label="Souls Infected" value={infections.toLocaleString()} onClick={spread} action="TAP TO SPREAD" />
        <StatCard label="Total Real Cases" value={stats.total_cases.toLocaleString()} />
        <StatCard label="Countries Hit" value={stats.countries} />
        <StatCard label="Documented Outbreaks" value={stats.outbreaks} />
      </div>
    </section>
  );
};

const StatCard = ({ label, value, onClick, action }) => (
  <div
    onClick={onClick}
    data-testid={`stat-${label.replace(/\s+/g, "-").toLowerCase()}`}
    className={`petri p-4 flex flex-col items-center ${onClick ? "cursor-pointer hover-shake" : ""}`}
  >
    <span className="stencil-pink text-3xl md:text-4xl">{value}</span>
    <span className="font-mono2 text-[10px] md:text-xs text-white/70 uppercase tracking-wider mt-1">{label}</span>
    {action && <span className="font-mono2 text-[9px] text-[#d9b3ff] mt-1 blink">{action}</span>}
  </div>
);

export default Hero;
